import { useCallback, useEffect, useState } from "react";
import firebase from "../../configs/FirebaseConfig";
import { useSelector } from "react-redux";

const db = firebase.firestore();

/**
 * Hook to fetch and manage CRM tasks from Firestore
 * @param {Object} options - Configuration options
 * @param {string} options.assignedToUid - Filter tasks by assigned user UID (optional)
 * @param {string} options.customerUserId - Filter tasks by customer user ID (optional)
 * @param {string} options.status - Filter tasks by status: 'open', 'done', or null for all (optional)
 * @param {boolean} options.autoFetch - Whether to automatically fetch data on mount (default: true)
 * @param {boolean} options.realtime - Whether to use real-time listener (default: true)
 * @returns {Object} - Tasks data and utility functions
 */
export const useCrmTasks = (options = {}) => {
  const { assignedToUid = null, customerUserId = null, status = null, autoFetch = true, realtime = true } = options;
  const auth = useSelector((state) => state.firebaseReducer.auth);

  const [tasks, setTasks] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Fetch CRM tasks based on filters
   * @param {Object} filters - Filter options
   * @returns {Promise<Array>} - Array of task documents
   */
  const fetchTasks = useCallback(async (filters = {}) => {
    setIsLoading(true);
    setError(null);

    try {
      let query = db.collectionGroup("crmTasks");

      // Apply filters
      if (filters.assignedToUid) {
        query = query.where("assignedToUid", "==", filters.assignedToUid);
      }
      if (filters.customerUserId) {
        query = query.where("customerUserId", "==", filters.customerUserId);
      }
      if (filters.status) {
        query = query.where("status", "==", filters.status);
      }

      // Order by creation date
      query = query.orderBy("createdAt", "desc");

      const snapshot = await query.get();

      if (snapshot.empty) {
        setTasks([]);
        return [];
      }

      const data = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      setTasks(data);
      return data;
    } catch (err) {
      console.error("Error fetching CRM tasks:", err);
      setError(err);
      return [];
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Get open tasks (status === 'open' or undefined)
   * @returns {Array} - Filtered open tasks
   */
  const getOpenTasks = useCallback(() => {
    return tasks.filter((task) => (task?.status || "open") === "open");
  }, [tasks]);

  /**
   * Get completed tasks (status === 'done')
   * @returns {Array} - Filtered completed tasks
   */
  const getCompletedTasks = useCallback(() => {
    return tasks.filter((task) => task?.status === "done");
  }, [tasks]);

  /**
   * Get overdue tasks (open tasks with dueAt in the past)
   * @returns {Array} - Filtered overdue tasks
   */
  const getOverdueTasks = useCallback(() => {
    const now = Math.floor(Date.now() / 1000);
    return tasks.filter((task) => {
      const isOpen = (task?.status || "open") === "open";
      const hasDueDate = typeof task?.dueAt === "number";
      return isOpen && hasDueDate && task.dueAt < now;
    });
  }, [tasks]);

  /**
   * Get tasks due soon (within next 24 hours)
   * @returns {Array} - Filtered tasks due soon
   */
  const getDueSoonTasks = useCallback(() => {
    const now = Math.floor(Date.now() / 1000);
    const soon = now + 24 * 60 * 60; // 24 hours from now
    return tasks.filter((task) => {
      const isOpen = (task?.status || "open") === "open";
      const hasDueDate = typeof task?.dueAt === "number";
      return isOpen && hasDueDate && task.dueAt >= now && task.dueAt <= soon;
    });
  }, [tasks]);

  // Set up real-time listener or fetch once
  useEffect(() => {
    if (!autoFetch) return;

    if (auth.isEmpty) {
      // Auth nog niet ready / niet ingelogd
      setTasks([]);
      return;
    }

    // Don't query if no filters are provided (would fetch all tasks)
    if (!assignedToUid && !customerUserId && !status) {
      console.warn("useCrmTasks: No filters provided, skipping query");
      setTasks([]);
      return;
    }

    const filters = {};
    if (assignedToUid) filters.assignedToUid = assignedToUid;
    if (customerUserId) filters.customerUserId = customerUserId;
    if (status) filters.status = status;

    if (realtime) {
      // Real-time listener
      setIsLoading(true);
      let query = db.collectionGroup("crmTasks");

      // Apply filters
      if (filters.assignedToUid) {
        query = query.where("assignedToUid", "==", filters.assignedToUid);
      }
      if (filters.customerUserId) {
        query = query.where("customerUserId", "==", filters.customerUserId);
      }
      if (filters.status) {
        query = query.where("status", "==", filters.status);
      }

      query = query.orderBy("createdAt", "desc");

      const unsubscribe = query.onSnapshot(
        (snapshot) => {
          const data = snapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));
          setTasks(data);
          setIsLoading(false);
          setError(null);
        },
        (err) => {
          console.error("useCrmTasks: Error in listener:", err);
          console.error("useCrmTasks: Error code:", err.code);
          console.error("useCrmTasks: Error message:", err.message);
          setError(err);
          setTasks([]);
          setIsLoading(false);
        },
      );

      return () => {
        if (unsubscribe) unsubscribe();
      };
    } else {
      // One-time fetch
      fetchTasks(filters);
    }
  }, [autoFetch, assignedToUid, customerUserId, status, realtime, fetchTasks]);

  return {
    tasks,
    isLoading,
    error,
    fetchTasks,
    getOpenTasks,
    getCompletedTasks,
    getOverdueTasks,
    getDueSoonTasks,
  };
};
